import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DTypeFormAddressField } from '@grupo-constant/next-dtype/form';
import { AddressByGoogle, DTypeViewableCol } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { DTypeFormFieldComponent } from '../../../dtype-field.model';
import { DialogsService } from '../../../../../dialogs/dialogs.service';
import { LocationService } from '../../../../location/location.service';
import { UserService } from '../../../../user/user.service';

@Component({
    selector: 'address-assitant-field-component',
    templateUrl: './address-assistant-field.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DTypeAddressAssistantFieldComponent implements DTypeFormFieldComponent, OnInit, OnDestroy {

    @Input() field: DTypeFormAddressField;
    @Input() isUpdate: boolean;
    @Input() useSummaryView: boolean;

    private _geopositionSummaryList: any[] = [];

    set geopositionSummaryList(value: any[]) {
        this._geopositionSummaryList = value;
        // Actualiza formWithLocation cada vez que el punto cambie en el mapa
        this.updateFormFromGeoposition();
    }

    get geopositionSummaryList() {
        return this._geopositionSummaryList;
    }

    ignoreCache = false;

    searchForm = new FormGroup({
        address: new FormControl('')
    });

    formWithLocation = new FormGroup({
        latitude: new FormControl<number | null>(null, Validators.required),
        longitude: new FormControl<number | null>(null, Validators.required)
    });

    googleAddress: AddressByGoogle;

    private _unsubscribeAll = new Subject<void>();
    private destroy$ = new Subject<void>();

    constructor(
        private dialogsService: DialogsService,
        private locationService: LocationService,
        private transloco: TranslocoService,
        private userService: UserService,
    ) { }

    ngOnInit() {
        const address = this.field.control.value;

        if (address) {
            this.formWithLocation.setValue({
                latitude: address.latitude,
                longitude: address.longitude
            });
            this.reloadMap();
        }

        const readonlyOnUpdate = (this.field.dTypeCol as DTypeViewableCol).readonlyOnUpdate;
        if (readonlyOnUpdate && this.isUpdate) {
            this.field.readonly = true;
        }

        this.formWithLocation.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(value => {
                this.field.control.patchValue(value, { emitEvent: false });
            });
    }

    ngOnDestroy() {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    async search() {
        const address = this.searchForm.get('address').value
        const parts = address.split(',');

        if (parts.length === 2 && !isNaN(Number(parts[0])) && !isNaN(Number(parts[1]))) {
            const latitude = parseFloat(parts[0]);
            const longitude = parseFloat(parts[1]);

            this.formWithLocation.setValue({
                latitude,
                longitude
            });
            this.reloadMap();
            this.searchForm.reset();
            this.googleAddress = null;
        } else {
            this.searchWithAddress(address);
        }
    }

    /**
     * Get addresses with the introduced text
     */
    async searchWithAddress(address: string) {
        const places = await firstValueFrom(this.locationService.retrieveGoogleAddress({
            completeAddress: address,
            searchLanguage: this.userService.user?.preferredLanguage?.substring(0, 2),
            api: this.field.api,
            ignoreCache: this.ignoreCache
        }));
        this.fillAddress(places);
    }

    private fillAddress(places: AddressByGoogle[]) {
        if (places.length > 0) {
            if (places.length === 1) {
                const place = places[0];
                this.googleAddress = place;
                if (this.googleAddress.latitude && this.googleAddress.longitude) {
                    this.formWithLocation.setValue({
                        latitude: this.googleAddress.latitude,
                        longitude: this.googleAddress.longitude
                    });
                    this.reloadMap();
                }
            } else {
                const dialog = this.dialogsService.openAddressListDialog(places);
                dialog.afterClosed().subscribe((result) => {
                    if (result) {
                        this.googleAddress = result;
                        if (this.googleAddress.latitude && this.googleAddress.longitude) {
                            this.formWithLocation.setValue({
                                latitude: this.googleAddress.latitude,
                                longitude: this.googleAddress.longitude
                            });
                            this.reloadMap();
                        }
                    }
                });
            }
        } else {
            this.dialogsService.openErrorDialog({
                message: this.transloco.translate('noAddressFound')
            });
        }
    }

    reloadMap() {
        this.geopositionSummaryList = [{
            latitude: this.formWithLocation.value.latitude,
            longitude: this.formWithLocation.value.longitude
        }];
    }

    canReloadMap() {
        return this.formWithLocation.value.latitude && this.formWithLocation.value.longitude
    }

    // Cambio de geopositionSummaryList en SingleGeopositionMapComponent
    onGeopositionSummaryListChanged(updatedList: any[]) {
        if (updatedList === this._geopositionSummaryList) {
            return;
        }
        this.geopositionSummaryList = updatedList;
    }

    private updateFormFromGeoposition() {
        if (this.geopositionSummaryList && this.geopositionSummaryList.length > 0) {
            const position = this.geopositionSummaryList[0];
            
            this.formWithLocation.patchValue({
                latitude: position.latitude,
                longitude: position.longitude
            });
        }
    }
}
